import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, BarChart3 } from 'lucide-react';
import { AWARD_LABELS } from './qbAwards';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Movement = ({ rank, previous }) => {
  if (!previous || previous === rank) return <span className="text-slate-400 text-sm">—</span>;
  const up = rank < previous;
  return (
    <span className={`text-sm font-bold ${up ? 'text-green-600' : 'text-red-500'}`} data-testid="rank-movement">
      {up ? '▲' : '▼'}{Math.abs(previous - rank)}
    </span>
  );
};

const Avatar = ({ qb }) => qb.photo_url ? (
  <img src={qb.photo_url} alt={qb.name} className="w-14 h-14 rounded-full object-cover border-2" style={{ borderColor: qb.color }} />
) : (
  <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: qb.color }}>
    {qb.name?.charAt(0)}
  </div>
);

const Stat = ({ value, label }) => (
  <div className="text-center">
    <div className="text-xl font-bold text-[#001f3f]">{value}</div>
    <div className="text-xs text-slate-500 uppercase">{label}</div>
  </div>
);

const RankingRow = ({ qb }) => {
  const isQBOW = qb.awards.includes('qb_of_week');
  return (
    <div
      className={`bg-white rounded-xl shadow p-4 flex items-center gap-4 border-l-4 ${isQBOW ? 'ring-2 ring-yellow-400' : ''}`}
      style={{ borderLeftColor: qb.color }}
      data-testid={`ranking-row-${qb.player_id}`}
    >
      <div className="text-3xl font-bold w-12 text-center text-[#001f3f]" data-testid={`ranking-rank-${qb.player_id}`}>#{qb.rank}</div>
      <Avatar qb={qb} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-bold text-lg text-[#001f3f]">{qb.name}</h3>
          <Movement rank={qb.rank} previous={qb.previous_rank} />
        </div>
        <p className="text-sm text-slate-500">
          {qb.school}{qb.result && <> • <span className="font-semibold">{qb.result}</span> {qb.opponent}</>}{qb.team_record && <> • {qb.team_record}</>}
        </p>
        <div className="flex flex-wrap gap-1 mt-1">
          {qb.awards.map(a => (
            <span key={a} className="text-xs bg-yellow-400 text-black px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`ranking-award-${qb.player_id}-${a}`}>
              <Trophy className="w-3 h-3" /> {AWARD_LABELS[a]}
            </span>
          ))}
        </div>
      </div>
      <div className="hidden md:flex gap-6">
        <Stat value={qb.passing_efficiency?.toFixed(1) ?? '—'} label="Effic" />
        <Stat value={(qb.passing_yards || 0).toLocaleString()} label="Pass Yds" />
        <Stat value={qb.passing_tds || 0} label="Pass TD" />
        <Stat value={qb.rushing_synced ? qb.rushing_yards : '—'} label="Rush Yds" />
      </div>
    </div>
  );
};

export function QBRankingsTab({ selectedWeek }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!selectedWeek) return;
    setData(null);
    axios.get(`${API}/qb/rankings/${selectedWeek}`).then(r => setData(r.data)).catch(() => setData({ rankings: [] }));
  }, [selectedWeek]);

  if (!data) return <div className="text-center py-12 text-slate-500">Loading rankings...</div>;

  return (
    <div data-testid="rankings-section">
      <h2 className="text-2xl font-bold text-[#001f3f] mb-2 flex items-center gap-2">
        <BarChart3 className="h-6 w-6" /> Week {selectedWeek} Rankings
      </h2>
      <p className="text-sm text-slate-500 mb-6">Ranked by SWAC passer efficiency (season through Week {selectedWeek}). Arrows show movement from last week.</p>
      {data.rankings.length === 0 ? (
        <div className="text-center py-12 text-slate-500" data-testid="rankings-empty">No rankings available for Week {selectedWeek}.</div>
      ) : (
        <div className="space-y-3">{data.rankings.map(qb => <RankingRow key={qb.player_id} qb={qb} />)}</div>
      )}
    </div>
  );
}
